// My Syndicates List Page — matches All Accounts / All Contacts table format exactly
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchTenantUsers } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Plus, Search, CircleStar, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { TableToolbar } from '../../components/common/TableToolbar';

export default function MySyndicatesPage() {
  const navigate = useNavigate();
  const { isViewOnly, isSyndicator } = useAuth();
  const viewOnly = isViewOnly();
  const qc = useQueryClient();

  useEffect(() => {
    if (isSyndicator === false) navigate('/dashboard', { replace: true });
  }, [isSyndicator, navigate]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterManager, setFilterManager] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newManager, setNewManager] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: syndicates = [], isLoading: loading } = useQuery({
    queryKey: QK.managedSyndicates(),
    queryFn: () => api.get('/crm-entities/managed-syndicates').then(r => r.data),
    onError: () => toast.error('Failed to load syndicates'),
  });

  const { data: usersData = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: fetchTenantUsers(),
  });
  const users = usersData;

  const invalidate = () => qc.invalidateQueries({ queryKey: QK.managedSyndicates() });

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Syndicate name is required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/crm-entities/managed-syndicates', {
        name: newName.trim(),
        manager_user_id: newManager || '',
      });
      toast.success('Syndicate created');
      setCreateOpen(false);
      setNewName(''); setNewManager('');
      navigate(`/syndicates/${res.data.id}`); invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create syndicate');
    } finally {
      setSaving(false);
    }
  };

  const userMap = Object.fromEntries(
    users.map(u => [u.id, `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email])
  );

  // Manager options for filter (only managers who appear in the list)
  const managerOptions = users.filter(u =>
    syndicates.some(s => s.account_manager_id === u.id)
  );

  const filtered = syndicates.filter(s => {
    const matchesSearch = !searchTerm.trim() ||
      s.system_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesManager = filterManager === 'all' || s.account_manager_id === filterManager;
    return matchesSearch && matchesManager;
  });

  if (loading) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6" data-testid="syndicates-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold">My Syndicates</h1>
            <p className="text-muted-foreground mt-1">All Managed Syndicates</p>
          </div>
          {!viewOnly && (
            <Button
              onClick={() => setCreateOpen(true)}
              className="btn-primary w-full sm:w-auto"
              data-testid="new-syndicate-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Syndicate
            </Button>
          )}
        </div>

        <TableToolbar
          tableId="syndicates-list"
          data={filtered}
          filename="syndicates"
          columns={[
            { key: 'system_name', label: 'Syndicate Name' },
            { key: 'account_manager_id', label: 'Manager' },
          ]}
        />

        {/* Table */}
        <Card>
          <Table className="table-heritage" data-table-id="syndicates-list">
            <TableHeader>
              <TableRow>
                {/* Syndicate Name with search */}
                <TableHead className="min-w-[300px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Syndicate Name</span>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="h-8 pl-8 text-xs bg-white"
                        data-testid="search-syndicates-input"
                      />
                    </div>
                  </div>
                </TableHead>

                {/* Manager with filter */}
                <TableHead className="min-w-[200px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Manager</span>
                    <Select value={filterManager} onValueChange={setFilterManager}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-manager-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {managerOptions.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {userMap[u.id]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                <TableHead className="w-[50px] bg-muted/20"></TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-12">
                    <CircleStar className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-muted-foreground">No syndicates found</p>
                    {!viewOnly && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => setCreateOpen(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" /> Create First Syndicate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(s => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/syndicates/${s.id}`)}
                    data-testid={`syndicate-row-${s.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary flex items-center justify-center shrink-0">
                          <CircleStar className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{s.system_name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {s.account_manager_id ? (userMap[s.account_manager_id] || '-') : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create Syndicate Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Syndicate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Syndicate Name <span className="text-red-500">*</span></Label>
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Sunrise Racing Syndicate"
                data-testid="syndicate-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Manager</Label>
              <Select value={newManager || '_none'} onValueChange={v => setNewManager(v === '_none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select a manager (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None —</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              className="btn-primary"
              onClick={handleCreate}
              disabled={saving || !newName.trim()}
              data-testid="create-syndicate-submit"
            >
              {saving ? 'Creating…' : 'Create Syndicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
}
